import React from "react";
import { ChevronDown, Smartphone } from "lucide-react";
import { StatusBadge, DISPATCH_ROUTE_PILL_RADIUS_CLASS_NAME, joinClasses } from "./ResultsPrimitives.jsx";
import FleetTruckStatusIconHoverCard from "./FleetTruckStatusIconHoverCard.jsx";
import FleetTruckStatusIndicator from "./FleetTruckStatusIndicator.jsx";
import { getFleetProductTone } from "../utils/cropColors.js";
import {
  DRIVER_PORTAL_FLUORESCENT_BADGE_CLASS_NAME,
  DRIVER_PORTAL_FLUORESCENT_ICON_CLASS_NAME,
} from "../utils/driverPortalPublicationStyles.js";
import { buildDispatchDisabledHoverCardContent, buildTruckProtectionHoverCardContent } from "../utils/fleetTruckStatusHoverCardContent.js";
import {
  REASSIGNMENT_POINTER_HOLD_DELAY_MS,
  REASSIGNMENT_POINTER_MOVE_THRESHOLD_PX,
} from "../hooks/useFleetReassignmentDrag.js";

const ASSIGNMENT_PILL_BASE_CLASS_NAME = `inline-flex min-w-0 items-center overflow-hidden border px-1.25 py-0.5 text-[0.6rem] font-extrabold uppercase leading-none tracking-[0.02em] ${DISPATCH_ROUTE_PILL_RADIUS_CLASS_NAME}`;

export default function AssignmentHeader({
  isOpen = true,
  isSelected = false,
  isTruckActionsOpen = false,
  onCloseTruckActions = null,
  onOpenTruckActions = null,
  onSelect = null,
  onToggle = null,
  portalPublication = null,
  shell,
}) {
  const isUnassigned = shell.statusBadge.label === "Sans tournée" && !shell.statusBadge.isHidden;
  const grainTone = getFleetProductTone(shell.grainTypeLabel);
  const manualMixProductLabels = Array.isArray(shell.manualMixBadge?.products)
    ? shell.manualMixBadge.products
      .map((productLabel) => (typeof productLabel === "string" ? productLabel.trim() : ""))
      .filter(Boolean)
    : [];
  // Plusieurs produits : une 2e ligne, pour les lire en entier.
  const wrapsProductLabels = manualMixProductLabels.length > 1;
  const truckProtectionHoverCard = React.useMemo(
    () => buildTruckProtectionHoverCardContent(shell.truckProtectionBadge),
    [shell.truckProtectionBadge],
  );
  const dispatchDisabledHoverCard = React.useMemo(
    () => buildDispatchDisabledHoverCardContent(shell.dispatchDisabledBadge),
    [shell.dispatchDisabledBadge],
  );
  const truckHoldTimerRef = React.useRef(null);
  const truckHoldStartPointRef = React.useRef(null);
  const suppressNextSelectClickRef = React.useRef(false);

  const clearTruckActionHold = React.useCallback(() => {
    if (truckHoldTimerRef.current && typeof window !== "undefined") {
      window.clearTimeout(truckHoldTimerRef.current);
    }
    truckHoldTimerRef.current = null;
    truckHoldStartPointRef.current = null;
  }, []);

  React.useEffect(() => () => {
    clearTruckActionHold();
  }, [clearTruckActionHold]);

  const openTruckActions = React.useCallback(() => {
    suppressNextSelectClickRef.current = true;
    onSelect?.();
    onOpenTruckActions?.();
  }, [onOpenTruckActions, onSelect]);

  const handleSelect = (event) => {
    event.preventDefault();
    event.stopPropagation();
    if (suppressNextSelectClickRef.current) {
      suppressNextSelectClickRef.current = false;
      return;
    }
    onCloseTruckActions?.();
    onSelect?.();
  };
  const handleToggle = (event) => {
    event.preventDefault();
    event.stopPropagation();
    onCloseTruckActions?.();
    onToggle?.();
  };

  const handleSelectContextMenu = (event) => {
    if (typeof onOpenTruckActions !== "function") {
      return;
    }

    event.preventDefault();
    event.stopPropagation();
    clearTruckActionHold();
    openTruckActions();
  };

  const handleSelectPointerDown = (event) => {
    if (typeof onOpenTruckActions !== "function" || event.button !== 0 || !event.isPrimary || typeof window === "undefined") {
      return;
    }

    suppressNextSelectClickRef.current = false;
    clearTruckActionHold();
    truckHoldStartPointRef.current = {
      clientX: event.clientX,
      clientY: event.clientY,
    };
    truckHoldTimerRef.current = window.setTimeout(() => {
      truckHoldTimerRef.current = null;
      openTruckActions();
    }, REASSIGNMENT_POINTER_HOLD_DELAY_MS);
  };

  const handleSelectPointerMove = (event) => {
    if (!truckHoldTimerRef.current || !truckHoldStartPointRef.current) {
      return;
    }

    const deltaX = event.clientX - truckHoldStartPointRef.current.clientX;
    const deltaY = event.clientY - truckHoldStartPointRef.current.clientY;
    if (Math.hypot(deltaX, deltaY) > REASSIGNMENT_POINTER_MOVE_THRESHOLD_PX) {
      clearTruckActionHold();
    }
  };

  const handleSelectPointerEnd = () => {
    clearTruckActionHold();
  };

  return (
    <div
      className={joinClasses([
        "flex min-w-0 w-full items-center gap-0.5 rounded-[4px] border px-1.25 py-[0.28rem] transition duration-150 focus-within:outline-hidden focus-within:ring-2 focus-within:ring-accent focus-within:ring-offset-2",
        isSelected
          ? "border-legacy-teal bg-legacy-teal shadow-none hover:bg-[#0c4b47]"
          : isTruckActionsOpen
            ? "border-slate-400 bg-slate-100 shadow-none"
            : "border-[rgba(71,85,105,0.16)] bg-white shadow-none hover:border-[rgba(71,85,105,0.22)] hover:bg-gray-50",
      ])}
    >
      <button
        type="button"
        className={joinClasses([
          "flex min-w-0 flex-1 items-center justify-between gap-0.75 text-left",
          wrapsProductLabels ? "flex-wrap gap-y-0.75" : "",
        ])}
        onClick={handleSelect}
        data-dispatch-truck-context="true"
        onContextMenu={handleSelectContextMenu}
        onPointerCancel={handleSelectPointerEnd}
        onPointerDown={handleSelectPointerDown}
        onPointerLeave={handleSelectPointerEnd}
        onPointerMove={handleSelectPointerMove}
        onPointerUp={handleSelectPointerEnd}
        aria-label={`Sélectionner ${shell.truckTitle}`}
      >
        <h3 className={joinClasses([
          "min-w-0 flex-1 truncate pr-1 text-[12px] font-semibold leading-[1.1]",
          isSelected ? "text-white" : "text-content-ink",
        ])}>
          {shell.truckTitle}
        </h3>

        <div className={joinClasses([
          "flex min-w-0 shrink items-center gap-0.5",
          wrapsProductLabels ? "basis-full flex-wrap" : "max-w-[10.8rem] overflow-hidden",
        ])}>
          {!shell.statusBadge.isHidden ? (
            <StatusBadge
              tone={shell.statusBadge.tone}
              className={joinClasses([
                "min-w-0 justify-start overflow-hidden px-1.25 py-0.5 text-[0.6rem] leading-none",
                DISPATCH_ROUTE_PILL_RADIUS_CLASS_NAME,
                isUnassigned ? "shrink-0 border border-slate-300 bg-slate-100 normal-case! tracking-normal! text-slate-600" : "max-w-22",
              ])}
              data-assignment-status
              title={shell.statusBadge.label}
            >
              <span className="truncate">{shell.statusBadge.label}</span>
            </StatusBadge>
          ) : null}
          {shell.dispatchDisabledBadge ? (
            <FleetTruckStatusIconHoverCard
              accentColor={dispatchDisabledHoverCard?.accentColor}
              description={dispatchDisabledHoverCard?.description}
              title={dispatchDisabledHoverCard?.title}
            >
              <FleetTruckStatusIndicator
                className={isSelected ? "[&_svg]:text-white/82" : ""}
                isEndOfDayLoadTransit={shell.dispatchDisabledBadge.isEndOfDayLoadTransit}
                title={shell.dispatchDisabledBadge.title}
                variant="dispatch-disabled"
              />
            </FleetTruckStatusIconHoverCard>
          ) : null}
          {portalPublication ? (
            <span
              className={joinClasses([
                "inline-flex h-5 w-5 shrink-0 items-center justify-center rounded-full border",
                portalPublication.status === "published"
                  ? DRIVER_PORTAL_FLUORESCENT_BADGE_CLASS_NAME
                  : isSelected
                    ? "border-white/18 bg-white/10 text-white/72"
                    : "border-slate-200 bg-slate-50 text-slate-400",
              ])}
              data-assignment-driver-portal-status={portalPublication.status}
              title={portalPublication.status === "published"
                ? portalPublication.isDispatchDisabled
                  ? "Portail actif · camion hors dispatch"
                  : "Tournée publiée dans le portail chauffeur"
                : "Tournée non publiée"}
            >
              <Smartphone
                aria-hidden="true"
                className={portalPublication.status === "published" ? DRIVER_PORTAL_FLUORESCENT_ICON_CLASS_NAME : "fill-slate-100"}
                size={11}
                strokeWidth={2.2}
              />
            </span>
          ) : null}
          {shell.truckProtectionBadge ? (
            <FleetTruckStatusIconHoverCard
              accentColor={truckProtectionHoverCard?.accentColor}
              description={truckProtectionHoverCard?.description}
              title={truckProtectionHoverCard?.title}
            >
              <FleetTruckStatusIndicator
                className={isSelected ? "[&_svg]:text-white/82" : ""}
                title={shell.truckProtectionBadge.title}
                variant="protected"
              />
            </FleetTruckStatusIconHoverCard>
          ) : null}
          {shell.manualMixBadge ? (
            manualMixProductLabels.length ? (
              manualMixProductLabels.map((productLabel) => {
                const { accentColor } = getFleetProductTone(productLabel);
                return (
                  <span
                    key={productLabel}
                    className={joinClasses([
                      ASSIGNMENT_PILL_BASE_CLASS_NAME,
                      "font-semibold text-[0.63rem] text-white",
                      wrapsProductLabels ? "" : "max-w-16",
                    ])}
                    data-assignment-manual-mix-product={productLabel}
                    style={{ backgroundColor: accentColor, borderColor: accentColor }}
                    title={productLabel}
                  >
                    <span className="truncate">{productLabel}</span>
                  </span>
                );
              })
            ) : (
              <span
                className={joinClasses([
                  ASSIGNMENT_PILL_BASE_CLASS_NAME,
                  "max-w-22",
                  isSelected
                    ? "border-white/24 bg-white/14 text-white"
                    : "border-border-warning-muted bg-amber-soft text-warning-ink",
                ])}
                data-assignment-manual-mix
                title={shell.manualMixBadge.title || undefined}
              >
                <span className="truncate">{shell.manualMixBadge.label}</span>
              </span>
            )
          ) : null}
          {shell.grainTypeLabel && !shell.manualMixBadge ? (
            <span
              className={joinClasses([
                ASSIGNMENT_PILL_BASE_CLASS_NAME,
                "max-w-16 font-semibold text-[0.63rem] text-white",
              ])}
              data-assignment-grain-type
              style={{ backgroundColor: grainTone.accentColor, borderColor: grainTone.accentColor }}
              title={shell.grainTypeLabel}
            >
              <span className="truncate">{shell.grainTypeLabel}</span>
            </span>
          ) : null}
        </div>
      </button>
      <button
        type="button"
        className={joinClasses([
          "inline-flex h-5.5 w-5.5 shrink-0 items-center justify-center rounded-full border transition duration-150",
          isSelected
            ? "border-white/22 bg-white/12 hover:bg-white/18"
            : "border-[rgba(71,85,105,0.18)] bg-white text-slate-400 hover:border-[rgba(71,85,105,0.3)] hover:text-slate-500",
        ])}
        data-assignment-toggle
        aria-expanded={isOpen ? "true" : "false"}
        aria-label={`${isOpen ? "Replier" : "Déplier"} ${shell.truckTitle}`}
        onClick={handleToggle}
      >
        <ChevronDown
          size={12}
          aria-hidden="true"
          className={joinClasses([
            "shrink-0 transition-transform duration-200",
            isSelected ? "text-white/82" : "text-current",
            isOpen ? "rotate-180" : "",
          ])}
        />
      </button>
    </div>
  );
}
